// ═══════════════════════════════════════════════════════════════
// Impact — Public Self-Report Page
// Citizens report property damage (no auth required)
// Route: /self-report/:incidentId
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, TextInput, Textarea, Select, Button,
  Group, Stack, Divider, Alert, Badge, FileButton, Image,
  ActionIcon, Checkbox, Stepper, Loader, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconAlertTriangle, IconCheck, IconChevronLeft, IconChevronRight,
  IconHome, IconSend, IconTrash, IconUpload, IconPhoto,
  IconUser, IconPhone, IconMail, IconMapPin, IconBuilding,
  IconUsers, IconHomeBolt, IconClipboardList,
} from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { resizeImageToBase64 } from '../intake/lib/helpers';
import { DAMAGE_QUESTIONS, calculateScore, scoreToCategory } from './lib/damageScore';
import '../../styles/public-form.css';

// ── Types ─────────────────────────────────────────────────────

interface AddressResult {
  display_name: string;
  address?: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
  };
}

const DWELLING_TYPES = [
  { value: 'Single-family', label: 'Single-family home' },
  { value: 'Multi-family', label: 'Multi-family' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Mobile Home', label: 'Mobile home' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Condo', label: 'Condo' },
];

const LIVING_OPTIONS = [
  { value: 'home', label: 'Still in my home', icon: IconHome },
  { value: 'family', label: 'With family/friends', icon: IconUsers },
  { value: 'shelter', label: 'At a shelter', icon: IconHomeBolt },
  { value: 'hotel', label: 'Hotel/motel', icon: IconBuilding },
  { value: 'vehicle', label: 'In my vehicle', icon: IconMapPin },
  { value: 'other', label: 'Other', icon: IconMapPin },
];

const STATE_MAP: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO',
  Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
  Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',
  Wyoming: 'WY',
};

function stateAbbr(s: string): string {
  return STATE_MAP[s] || s.substring(0, 2).toUpperCase();
}

// ── Main Component ────────────────────────────────────────────

export default function SelfReport() {
  const { incidentId } = useParams<{ incidentId: string }>();

  // Portal state
  const [portalLoading, setPortalLoading] = useState(true);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Stepper
  const [active, setActive] = useState(0);

  // Step 1: Contact / Property
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [dwellingType, setDwellingType] = useState('');
  const [occupants, setOccupants] = useState('');

  // Address autocomplete
  const [addrResults, setAddrResults] = useState<AddressResult[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 2: Damage questions
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [livingSituation, setLivingSituation] = useState('');
  const [hasInsurance, setHasInsurance] = useState<string | null>(null);
  const [hasFloodInsurance, setHasFloodInsurance] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  // Step 3: Photos & consent
  const [photos, setPhotos] = useState<{ file: File; preview: string }[]>([]);
  const [consent, setConsent] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportRef, setReportRef] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Portal loading ─────────────────────────────────────────

  useEffect(() => {
    if (incidentId) {
      setPortalLoading(false);
    } else {
      setPortalError('No incident ID provided. Please use the link provided by your local emergency management office.');
      setPortalLoading(false);
    }
  }, [incidentId]);

  // ── Address autocomplete ───────────────────────────────────

  const searchAddress = useCallback((q: string) => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (!q || q.length < 3) { setAddrResults([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=us&limit=5&addressdetails=1`,
          { headers: { 'User-Agent': 'OGEMC-Impact/1.0' } },
        );
        const results: AddressResult[] = await r.json();
        setAddrResults(results);
        setAddrOpen(results.length > 0);
      } catch { /* ignore */ }
    }, 350);
  }, []);

  const selectAddress = (result: AddressResult) => {
    const a = result.address;
    if (a) {
      setAddress(`${a.house_number || ''} ${a.road || ''}`.trim());
      setCity(a.city || a.town || a.village || '');
      setState(a.state ? stateAbbr(a.state) : '');
      setZip(a.postcode || '');
    } else {
      setAddress(result.display_name || '');
    }
    setAddrOpen(false);
  };

  // ── Damage score ───────────────────────────────────────────

  const score = calculateScore(answers);
  const { category, label: catLabel, color: catColor, description: catDesc } = scoreToCategory(score);
  const allQuestionsAnswered = DAMAGE_QUESTIONS.every(q => answers[q.id] !== undefined);

  // ── Photo handling ─────────────────────────────────────────

  const addPhotos = (files: File[]) => {
    const newPhotos = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 10));
  };

  const removePhoto = (idx: number) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  // ── Step validation ────────────────────────────────────────

  const canAdvance = (step: number): boolean => {
    if (step === 0) return !!(name.trim() && phone.trim() && address.trim() && city.trim() && zip.trim());
    if (step === 1) return allQuestionsAnswered && !!livingSituation;
    if (step === 2) return consent;
    return true;
  };

  const nextStep = () => { if (canAdvance(active)) setActive(a => Math.min(a + 1, 2)); };
  const prevStep = () => setActive(a => Math.max(a - 1, 0));

  // ── Submit ─────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!consent) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const reportId = await useImpactStore.getState().createSelfReport(
        {
          incidentId: incidentId || '',
          reporterName: name.trim(),
          reporterEmail: email.trim().toLowerCase() || '',
          reporterPhone: phone.trim() || '',
          address: address.trim(),
          city: city.trim(),
          stateCode: state.trim(),
          zip: zip.trim(),
          dwellingType: dwellingType || '',
          occupants: occupants || '',
        },
        {
          damageCategory: category,
          damageScore: String(score),
          insured: hasInsurance != null ? String(hasInsurance) : '',
          floodInsurance: hasFloodInsurance != null ? String(hasFloodInsurance) : '',
          livingSituation: livingSituation || '',
          notes: notes.trim() || '',
          consent: String(consent),
          reviewStatus: 'pending',
        },
      );

      // Upload any attached photos
      if (reportId && photos.length > 0) {
        const { uploadPhoto } = useImpactStore.getState();
        for (let i = 0; i < photos.length; i++) {
          try {
            const base64 = await resizeImageToBase64(photos[i].file, 1024);
            await uploadPhoto(reportId, base64, i, { caption: `Photo ${i + 1}`, takenBy: name.trim() });
          } catch { /* non-fatal — report still submitted */ }
        }
      }

      setReportRef(`SR-${reportId}`);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit report.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset ──────────────────────────────────────────────────

  const resetForm = () => {
    setActive(0);
    setName(''); setPhone(''); setEmail('');
    setAddress(''); setCity(''); setState(''); setZip('');
    setDwellingType(''); setOccupants('');
    setAnswers({}); setLivingSituation('');
    setHasInsurance(null); setHasFloodInsurance(null); setNotes('');
    photos.forEach(p => URL.revokeObjectURL(p.preview));
    setPhotos([]); setConsent(false);
    setSubmitted(false); setReportRef(''); setSubmitError(null);
  };

  // ── Shell wrapper ──────────────────────────────────────────

  const shell = (children: React.ReactNode) => (
    <div className="pf-page">
      <div className="pf-topbar">
        <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="pf-topbar-logo" />
        <div className="pf-topbar-divider" />
        <span className="pf-topbar-product">Emergency Management</span>
      </div>
      <div className="pf-content">
        <div className="pf-header-card">
          <div className="pf-header-icon">
            <IconClipboardList size={22} />
          </div>
          <div>
            <p className="pf-header-title">Report Property Damage</p>
            <p className="pf-header-sub">Help your community — report damage to your property</p>
          </div>
        </div>
        {children}
      </div>
      <p className="pf-footer">Powered by OpenGov Emergency Management Console</p>
    </div>
  );

  // ── Loading state ──────────────────────────────────────────

  if (portalLoading) {
    return shell(
      <div className="pf-card pf-state-wrap">
        <Loader size="lg" />
      </div>
    );
  }

  if (portalError) {
    return shell(
      <div className="pf-card pf-state-wrap">
        <div className="pf-state-icon pf-state-icon--error">
          <IconAlertTriangle size={30} />
        </div>
        <p className="pf-state-title">Not Available</p>
        <p className="pf-state-sub">{portalError}</p>
      </div>
    );
  }

  // ── Success screen ─────────────────────────────────────────

  if (submitted) {
    return shell(
      <div className="pf-card pf-state-wrap">
        <div className="pf-state-icon pf-state-icon--success">
          <IconCheck size={30} />
        </div>
        <p className="pf-state-title">Report Submitted</p>
        <p className="pf-state-sub">
          Thank you for reporting your property damage. Your report has been received and will
          be reviewed by damage assessment teams. You may be contacted for follow-up.
        </p>
        {reportRef && (
          <div className="pf-ref-box">
            <p className="pf-ref-label">Reference Number</p>
            <p className="pf-ref-number">{reportRef}</p>
            <p className="pf-ref-hint">Save this for your records</p>
          </div>
        )}
        <Button variant="light" onClick={resetForm}>
          Submit Another Report
        </Button>
      </div>
    );
  }

  // ── Main Form ──────────────────────────────────────────────

  return shell(
    <div className="pf-card">
      {/* Step indicator */}
      <Stepper active={active} onStepClick={setActive} size="sm" mb="xl" allowNextStepsSelect={false}>
        <Stepper.Step label="Your Info" icon={<IconUser size={16} />} />
        <Stepper.Step label="Damage" icon={<IconClipboardList size={16} />} />
        <Stepper.Step label="Review" icon={<IconCheck size={16} />} />
      </Stepper>

      {/* ── Step 1: Contact & Property ──────────────────────── */}
      {active === 0 && (
        <Stack gap="sm">
          <Box mb="sm">
            <Title order={4} mb={2}>Contact Information</Title>
            <Text size="sm" c="dimmed">This helps us follow up if needed. Name and phone are required.</Text>
          </Box>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="Full Name *"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              leftSection={<IconUser size={15} />}
            />
            <TextInput
              label="Phone *"
              placeholder="(555) 555-1234"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              leftSection={<IconPhone size={15} />}
            />
          </SimpleGrid>

          <TextInput
            label="Email"
            placeholder="email@example.com (optional)"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            leftSection={<IconMail size={15} />}
          />

          <Divider label="Property Information" labelPosition="left" my="xs" />

          <Box style={{ position: 'relative' }}>
            <TextInput
              label="Property Address *"
              placeholder="Start typing your address..."
              value={address}
              onChange={e => { setAddress(e.target.value); searchAddress(e.target.value); }}
              onBlur={() => setTimeout(() => setAddrOpen(false), 200)}
              leftSection={<IconMapPin size={15} />}
            />
            {addrOpen && addrResults.length > 0 && (
              <Paper
                shadow="md" radius="sm" withBorder
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  zIndex: 600, maxHeight: 180, overflowY: 'auto',
                }}
              >
                {addrResults.map((r, i) => (
                  <Box
                    key={i}
                    style={{
                      padding: '8px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--color-neutral-200)',
                      transition: 'background var(--transition-fast)',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-primary-50)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                    onMouseDown={() => selectAddress(r)}
                  >
                    <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.display_name}
                    </Text>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>

          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
            <TextInput label="City *" value={city} onChange={e => setCity(e.target.value)} />
            <TextInput label="State" placeholder="GA" maxLength={2} value={state}
              onChange={e => setState(e.target.value.toUpperCase())} />
            <TextInput label="ZIP *" placeholder="12345" maxLength={10} value={zip}
              onChange={e => setZip(e.target.value)} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select
              label="Dwelling Type"
              placeholder="Select..."
              data={DWELLING_TYPES}
              value={dwellingType || null}
              onChange={v => setDwellingType(v || '')}
              leftSection={<IconBuilding size={15} />}
              clearable
            />
            <TextInput
              label="Number of Occupants"
              placeholder="How many people live here?"
              type="number"
              min={0}
              value={occupants}
              onChange={e => setOccupants(e.target.value)}
              leftSection={<IconUsers size={15} />}
            />
          </SimpleGrid>

          <Group justify="flex-end" mt="md">
            <Button
              onClick={nextStep}
              disabled={!canAdvance(0)}
              rightSection={<IconChevronRight size={16} />}
            >
              Next — Damage Details
            </Button>
          </Group>
        </Stack>
      )}

      {/* ── Step 2: Damage Questions ─────────────────────────── */}
      {active === 1 && (
        <Stack gap="lg">
          <Box mb="xs">
            <Title order={4} mb={2}>Describe the Damage</Title>
            <Text size="sm" c="dimmed">Select the option that best describes each area</Text>
          </Box>

          {DAMAGE_QUESTIONS.map(q => (
            <Box key={q.id}>
              <Text fw={600} size="sm" mb="xs">{q.label}</Text>
              <Stack gap="xs">
                {(q.options || []).map(opt => {
                  const selected = answers[q.id] === opt.value;
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.value }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 16px', borderRadius: 'var(--radius-md)',
                        border: `1.5px solid ${selected ? 'var(--color-primary-700)' : 'var(--color-neutral-200)'}`,
                        background: selected ? 'var(--color-primary-50)' : 'white',
                        cursor: 'pointer',
                        transition: 'all var(--transition-fast)',
                      }}
                    >
                      <Box style={{
                        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${selected ? 'var(--color-primary-700)' : 'var(--color-neutral-400)'}`,
                        background: selected ? 'var(--color-primary-700)' : 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <Box style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
                      </Box>
                      <Text size="sm">{opt.label}</Text>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}

          <Divider />

          {/* Living situation */}
          <Box>
            <Text fw={600} size="sm" mb="xs">Where are you currently staying? *</Text>
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
              {LIVING_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const selected = livingSituation === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => setLivingSituation(opt.value)}
                    style={{
                      padding: '16px 8px', borderRadius: 'var(--radius-lg)', cursor: 'pointer',
                      border: `1.5px solid ${selected ? 'var(--color-primary-700)' : 'var(--color-neutral-200)'}`,
                      background: selected ? 'var(--color-primary-50)' : 'white',
                      textAlign: 'center',
                      transition: 'all var(--transition-fast)',
                    }}
                  >
                    <Icon size={22} color={selected ? 'var(--color-primary-700)' : 'var(--color-neutral-500)'}
                      style={{ margin: '0 auto' }} />
                    <Text size="xs" fw={600} mt={6}
                      style={{ color: selected ? 'var(--color-primary-800)' : 'inherit' }}>
                      {opt.label}
                    </Text>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select
              label="Homeowner Insurance?"
              placeholder="Select..."
              data={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'unknown', label: 'Not sure' },
              ]}
              value={hasInsurance}
              onChange={setHasInsurance}
              clearable
            />
            <Select
              label="Flood Insurance?"
              placeholder="Select..."
              data={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'unknown', label: 'Not sure' },
              ]}
              value={hasFloodInsurance}
              onChange={setHasFloodInsurance}
              clearable
            />
          </SimpleGrid>

          <Textarea
            label="Additional Details"
            placeholder="Describe any specific damage, safety concerns, or immediate needs..."
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />

          {allQuestionsAnswered && (
            <Alert color={catColor} variant="light" title="Preliminary Assessment">
              <Group gap="sm">
                <Badge color={catColor} size="lg">{catLabel}</Badge>
                <Text size="sm">{catDesc}</Text>
              </Group>
            </Alert>
          )}

          <Group justify="space-between" mt="md">
            <Button variant="light" color="#939598" onClick={prevStep} leftSection={<IconChevronLeft size={16} />}>
              Back
            </Button>
            <Button onClick={nextStep} disabled={!canAdvance(1)} rightSection={<IconChevronRight size={16} />}>
              Next — Review & Submit
            </Button>
          </Group>
        </Stack>
      )}

      {/* ── Step 3: Photos, Consent & Submit ─────────────────── */}
      {active === 2 && (
        <Stack gap="lg">
          <Box mb="xs">
            <Title order={4} mb={2}>Review & Submit</Title>
            <Text size="sm" c="dimmed">Confirm your information and submit your report</Text>
          </Box>

          {/* Summary */}
          <Paper p="md" radius="md" withBorder style={{ background: 'var(--color-neutral-50)' }}>
            <Text fw={600} size="sm" mb="sm" c="dimmed" tt="uppercase"
              style={{ letterSpacing: '0.04em', fontSize: '0.7rem' }}>
              Report Summary
            </Text>
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
              <SummaryField label="Name" value={name} />
              <SummaryField label="Phone" value={phone} />
              {email && <SummaryField label="Email" value={email} />}
              <SummaryField label="Address" value={address} />
              <SummaryField label="City / State / ZIP" value={`${city}, ${state} ${zip}`} />
              {dwellingType && <SummaryField label="Dwelling" value={dwellingType} />}
              {occupants && <SummaryField label="Occupants" value={occupants} />}
              <SummaryField label="Damage Level" value={catLabel} />
              <SummaryField label="Living Situation" value={livingSituation || '—'} />
              {hasInsurance && <SummaryField label="Insurance" value={hasInsurance} />}
              {hasFloodInsurance && <SummaryField label="Flood Ins." value={hasFloodInsurance} />}
            </Box>
            {notes && (
              <Box mt="sm" pt="sm" style={{ borderTop: '1px solid var(--color-neutral-200)' }}>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Notes</Text>
                <Text size="sm">{notes}</Text>
              </Box>
            )}
          </Paper>

          {/* Photos (display only) */}
          <Box>
            <Text fw={600} size="sm" mb={4}>
              <IconPhoto size={15} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Photos (optional)
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              Photos will be submitted with your report and reviewed by assessment staff.
            </Text>
            {photos.length > 0 && (
              <Group gap="xs" mb="xs" wrap="wrap">
                {photos.map((p, i) => (
                  <Box key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                    <Image src={p.preview} w={80} h={80} radius="sm" fit="cover" />
                    <ActionIcon
                      size="xs" color="var(--color-error)" variant="filled"
                      style={{ position: 'absolute', top: 2, right: 2 }}
                      onClick={() => removePhoto(i)}
                    >
                      <IconTrash size={11} />
                    </ActionIcon>
                  </Box>
                ))}
              </Group>
            )}
            {photos.length < 10 && (
              <FileButton
                onChange={files => files && addPhotos(Array.isArray(files) ? files : [files])}
                accept="image/*"
                multiple
              >
                {(props) => (
                  <Button variant="light" size="xs" leftSection={<IconUpload size={13} />} {...props}>
                    {photos.length > 0 ? 'Add More Photos' : 'Add Photos'}
                  </Button>
                )}
              </FileButton>
            )}
          </Box>

          <Divider />

          {/* Consent */}
          <Paper p="md" radius="md" withBorder
            style={{ background: 'var(--color-primary-50)', borderColor: 'var(--color-primary-200)' }}>
            <Checkbox
              checked={consent}
              onChange={e => setConsent(e.currentTarget.checked)}
              label="I certify that the information provided is true and accurate to the best of my knowledge. I understand this report will be used for preliminary damage assessment purposes and consent to being contacted by emergency management officials regarding this report."
              styles={{ label: { fontSize: 14, lineHeight: 1.5 } }}
            />
          </Paper>

          {submitError && (
            <Alert color="var(--color-error)" icon={<IconAlertTriangle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="space-between" mt="md">
            <Button variant="light" color="#939598" onClick={prevStep} leftSection={<IconChevronLeft size={16} />}>
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!consent || submitting}
              loading={submitting}
              leftSection={<IconSend size={16} />}
            >
              Submit Report
            </Button>
          </Group>
        </Stack>
      )}
    </div>
  );
}

// ── Summary field helper ─────────────────────────────────────

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <Box style={{ paddingBottom: 4 }}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.03em', fontSize: '0.68rem' }}>
        {label}
      </Text>
      <Text size="sm" fw={500}>{value}</Text>
    </Box>
  );
}
